import './InviteTeamMembersDialog.scss';

import * as React from 'react';
import { FormEvent, useState } from 'react';

import { SlOption, SlSelect } from '@shoelace-style/shoelace/dist/react';
import { PlanType, TeamRole } from '@showbie-socrative/socrative-utils';
import { useAtomValue, useSetAtom } from 'jotai';
import { atomWithReset } from 'jotai/utils';

import { sendTeacherAmplitudeEvent } from '~analytics/amplitude';
import { EventTypes } from '~analytics/types';
import { translate } from '~l10n/client';
import { AlertMessage } from '~shared/components/AlertMessage';
import { SmallButton } from '~shared/components/buttons';
import { Dialog } from '~shared/components/Dialog';
import { Divider } from '~shared/components/Divider';
import { toast } from '~shared/components/Toast';
import { selectedTeamIdAtom } from '~teacher/components/Account/atoms';
import { countSeatsUsed } from '~teacher/components/Account/helpers';
import SlChipsInput from '~teacher/components/Account/SlChipsInput/SlChipsInput';
import {
  OverPurchasedSeatsDialog,
  useDidOverInvitePurchasedSeats,
} from '~teacher/components/Account/TeamMembers/TeamMembersTable/AddSeatsDialog';
import {
  InviteOverPlanSeatLimitDialog,
  useDidInviteOverPlanSeatLimit,
} from '~teacher/components/Account/TeamMembers/TeamMembersTable/AddSeatsDialog/InviteOverPlanSeatLimitDialog';
import {
  useSelectedTeamHasActiveSubscriptionQuery,
  useSelectedTeamPermissionQuery,
} from '~teacher/components/PricingTable/hooks';
import {
  useCreateTeamInvite,
  useTeams,
  useTeamsMembers,
} from '~teacher/hook/team';

import { isValidEmail } from '../../../../../../../shared/isValidEmail';
import { sleep } from '../../../../util/sleep';

const MAX_EMAILS_SENT = 10;

interface TeamRoleOption {
  value: TeamRole;
  label: string;
  subLabel: string;
}

/*
 *  returns true if some emails are invalid.
 *  @param emails - string of emails separated by space
 */
export function checkSomeEmailInvalid(emails: string[]): boolean {
  if (emails.length === 0) {
    return true;
  }

  return emails
    .map((email) => email.trim())
    .filter(Boolean)
    .some((email) => !isValidEmail(email));
}

interface InviteTeamMembersDialogProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: string;
  isSkippable?: boolean;
}

// This atom is to store the sent email count to use in other dialogs later.
export const emailCountAtom = atomWithReset<number>(0);

export function useSeatsUsedForSelectedTeam() {
  const selectedTeamId = useAtomValue(selectedTeamIdAtom);
  return useTeamsMembers({
    teamId: selectedTeamId,
    select: (data) => countSeatsUsed(data),
  });
}

export function InviteTeamMembersDialog({
  onClose,
  isOpen,
  teamId,
  isSkippable = false,
}: InviteTeamMembersDialogProps) {
  const selectedTeamId = useAtomValue(selectedTeamIdAtom);
  const teamRoleQuery = useTeams({
    select: (data) =>
      data.find(({ id }) => `${id}` === `${selectedTeamId}`)?.role,
  });
  const teamSubscriptionsPermissionQuery = useSelectedTeamPermissionQuery();

  const selectedTeamHasActiveSubscriptionQuery =
    useSelectedTeamHasActiveSubscriptionQuery();

  const [isLoading, setIsLoading] = useState(false);
  const setEmailCount = useSetAtom(emailCountAtom);

  const openInviteOverPlanSeatLimit =
    InviteOverPlanSeatLimitDialog.useOpenDialog();
  const openOverPurchasedSeatsDialog = OverPurchasedSeatsDialog.useOpenDialog();

  const inviteMembersMutation = useCreateTeamInvite();
  const [role, setRole] = useState(`${TeamRole.Member}`);
  const [emails, setEmails] = useState([]);
  const [isSendingDisabled, setIsSendingDisabled] = useState(false);

  const emailCount = emails.length;

  const { didInviteOverPlanSeatLimit, planMaxSeatCountQuery } =
    useDidInviteOverPlanSeatLimit();

  const { didOverInvitePurchasedSeats } =
    useDidOverInvitePurchasedSeats(selectedTeamId);

  const someEmailInvalid = checkSomeEmailInvalid(emails);

  // Do not allow member to invite team admin
  const teamRoleOptions: TeamRoleOption[] = [
    ...(teamRoleQuery?.data !== TeamRole.Member
      ? [
          {
            label: translate('Team Admin'),
            subLabel: translate(
              'Can manage billing, team settings, and members'
            ),
            value: TeamRole.Admin,
          },
        ]
      : []),
    {
      label: translate('Team Member'),
      subLabel: translate('Can invite team members'),
      value: TeamRole.Member,
    },
  ];

  const sendInviteEmails = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSendingDisabled(true);
    setIsLoading(true);

    if (someEmailInvalid) {
      setIsLoading(false);
      toast.critical(translate('Invalid email. Please check input.'));
      return;
    }

    inviteMembersMutation.mutate(
      {
        teamId: teamId,
        emails: emails.map((email) => email.trim().toLowerCase()),
        role: Number.parseInt(role, 10),
      },
      {
        onSuccess: () => {
          onClose();

          toast(translate('Sent'));

          setEmails([]);
          sleep(1000).then(() => setIsSendingDisabled(false));

          sendTeacherAmplitudeEvent(EventTypes.TeamsSendInvites, {
            team_id: teamId,
            role_of_invites: TeamRole[role],
            number_of_invites: emails.length,
          });

          const { canOpenStripePortal } = teamSubscriptionsPermissionQuery.data;

          // Access to the stripe portal is required to upgrade and downgrade seats.
          if (!canOpenStripePortal) {
            return;
          }

          // check if plan is expired
          if (!selectedTeamHasActiveSubscriptionQuery.data) {
            return; // No plan, cannot show manage subscription dialog
          }

          if (
            didInviteOverPlanSeatLimit(emailCount) &&
            // Essentials and free are the only ones that you can upgrade
            [PlanType.Essentials, PlanType.Free].includes(
              planMaxSeatCountQuery.data?.planType
            )
          ) {
            openInviteOverPlanSeatLimit();
          } else if (didOverInvitePurchasedSeats(emailCount)) {
            setEmailCount(emailCount);
            openOverPurchasedSeatsDialog();
          }
        },
        onSettled: () => {
          setIsLoading(false);
        },
        onError: (error) => {
          if (__DEV__) {
            console.error(error);
          }
          toast.critical(translate('Send failed'));
          setTimeout(() => setIsSendingDisabled(false), 1000);
        },
      }
    );
  };

  // Helper function to check if an email is valid after trimming whitespace
  const isValidEmailTrimmed = (value: string) => isValidEmail(value.trim());

  // Disable controls if sending is disabled, no emails, or over the max email count
  return (
    <Dialog
      id="invite-members-dialog"
      open={isOpen}
      onClose={onClose}
      title={translate('Invite Members')}
    >
      <div className="pb-5 pt-3">
        <Divider />
      </div>
      <div>
        <p className="font-sans text-base text-blue-900">
          {translate(
            'Share a plan with others. Users can only access their own content.'
          )}
        </p>
      </div>
      <div className="py-5">
        <Divider />
      </div>
      <form className="grid gap-8" onSubmit={sendInviteEmails}>
        <div className="grid gap-5">
          <label className="flex flex-col gap-3 font-sans text-base font-400 leading-8">
            {translate('Type or paste emails')}
            <SlChipsInput
              checkValidValue={isValidEmailTrimmed}
              placeholder={''}
              values={emails}
              setValues={setEmails}
              validationMessage={translate(
                'Invalid email. Please check input.'
              )}
            />
          </label>
          <SlSelect
            label={translate('Invite as')}
            className="role-select"
            value={role}
            onSlChange={(event) =>
              setRole(`${(event.target as HTMLSelectElement).value}`)
            }
          >
            {teamRoleOptions.map(({ value, label, subLabel }) => (
              <SlOption
                className="role-option"
                value={`${value}`}
                key={value}
                getTextLabel={() => label}
              >
                <div className="flex flex-col">
                  <span className="font-sans text-base leading-8">{label}</span>
                  <span className="font-sans text-xs text-grey-900">
                    {subLabel}
                  </span>
                </div>
              </SlOption>
            ))}
          </SlSelect>
          {emailCount > MAX_EMAILS_SENT ? (
            <AlertMessage showIcon tone="critical">
              {translate('You can invite up to {0} people at a time.').format(
                MAX_EMAILS_SENT
              )}
            </AlertMessage>
          ) : null}
        </div>

        <div className="flex justify-end gap-4">
          {isSkippable ? (
            <SmallButton
              appearance="default"
              onClick={onClose}
              type="button"
              isDisabled={isSendingDisabled}
            >
              {translate('Skip')}
            </SmallButton>
          ) : null}
          <SmallButton
            appearance="primary"
            isLoading={isLoading}
            type="submit"
            isDisabled={
              isSendingDisabled ||
              emailCount === 0 ||
              emailCount > MAX_EMAILS_SENT
            }
          >
            {translate('Invite')}
          </SmallButton>
        </div>
      </form>
    </Dialog>
  );
}
